import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  DragIndicator,
  KeyboardArrowDown,
  KeyboardArrowRight,
  KeyboardArrowUp,
  MoreHoriz,
} from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { differenceInDays, isWithinInterval } from 'date-fns';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BASE_COLOR, getContrastTextColor } from 'src/lib/utils/roadmapping/colors';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { PARENT_HEADER_HEIGHT, VERTICAL_ROW_HEADER_WIDTH } from './useTimelineColumnDayWidth';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';

export const HeaderOptionsMenu = memo(
  ({
    isVertical,
    color,
    toggleReorderModal,
  }: {
    isVertical: boolean;
    color: string;
    toggleReorderModal: () => void;
  }) => {
    const { t } = useTranslation();
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const handleOpenChange = (open: boolean) => {
      setMenuOpen(open);
      setTooltipOpen(false);
    };

    return (
      <div
        className={cn('absolute', {
          'top-0.5 left-1/2 -translate-x-1/2': isVertical,
          'right-0.5 top-[calc(50%+2px)] -translate-y-1/2': !isVertical,
        })}
      >
        <Tooltip open={tooltipOpen && !menuOpen} onOpenChange={setTooltipOpen}>
          <Popover open={menuOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  style={{
                    color: getContrastTextColor(color),
                    backgroundColor: lightenTimelineBgColor(color, 30),
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={cn(
                    'opacity-0 group-hover:opacity-100 hover:bg-transition-opacity h-6 w-6',
                    {
                      'opacity-100': menuOpen,
                    },
                  )}
                >
                  <MoreHoriz />
                </Button>
              </TooltipTrigger>
            </PopoverTrigger>
            <PopoverContent className="p-0" side="bottom" align="start" sideOffset={0}>
              <Button
                variant="ghost"
                className="w-full items-start justify-start"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReorderModal();
                }}
              >
                <DragIndicator className="w-6 h-6 text-text-neutral-primary cursor-move" />
                {t('views.swimlane.headerMenu.reorderHeaders')}
              </Button>
            </PopoverContent>
          </Popover>

          <TooltipContent
            side={isVertical ? 'right' : 'bottom'}
            align="center"
            sideOffset={6}
            className="text-sm bg-bg-surface-base text-text-neutral-primary border-2"
          >
            {t('views.visualization.header.options')}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  },
);

export const HeaderTooltip = memo(
  ({
    label,
    isVertical,
    isExpanded,
  }: {
    label: string;
    isVertical: boolean;
    isExpanded?: boolean;
  }) => {
    const includeExpansionIcon = isExpanded !== undefined;
    return (
      <Tooltip>
        <TooltipTrigger asChild className="group">
          <div className="flex flex-col items-center justify-center min-w-0 h-full w-full">
            {includeExpansionIcon && !isVertical ? (
              isExpanded ? (
                <KeyboardArrowUp className="w-5 h-5 text-text-neutral-inverted" />
              ) : (
                <KeyboardArrowDown className="w-5 h-5 text-text-neutral-inverted" />
              )
            ) : null}

            {includeExpansionIcon && isVertical ? (
              isExpanded ? (
                <KeyboardArrowRight className="w-5 h-5 text-text-neutral-inverted" />
              ) : (
                <KeyboardArrowDown className="w-5 h-5 text-text-neutral-inverted" />
              )
            ) : null}
            <span
              className={cn(
                'inline-block text-center align-middle font-semibold text-text-neutral-inverted uppercase text-[0.725rem] min-w-0 truncate',
                {
                  '[writing-mode:vertical-rl] rotate-180': isVertical,
                  'w-full': !isVertical,
                  'p-1': !includeExpansionIcon,
                },
              )}
            >
              {label}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side={isVertical ? 'right' : 'bottom'}
          align="end"
          className="text-sm bg-bg-surface-base text-text-neutral-primary border-2"
        >
          {label}
        </TooltipContent>
      </Tooltip>
    );
  },
);

HeaderTooltip.displayName = 'HeaderTooltip';

export const VerticalParentRowHeader = memo(
  ({
    expanded,
    isCompact = false,
    label = '',
    color = DEFAULT_BASE_COLOR,
    totalHeight,
    onClick,
    toggleReorderModal,
  }: {
    expanded: boolean;
    isCompact?: boolean;
    label?: string;
    color?: string;
    totalHeight: number;
    onClick: () => void;
    toggleReorderModal?: () => void;
  }) => {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: needs to be interactive
      // biome-ignore lint/a11y/useKeyWithClickEvents: needs to be interactive
      <div
        className={cn(
          'group relative left-0 flex justify-center rounded-xs bg-bg-surface-base cursor-pointer top-0 right-0',
          {
            'rounded-xs': !isCompact,
          },
        )}
        style={{
          width: `${PARENT_HEADER_HEIGHT}px`,
          maxWidth: `${PARENT_HEADER_HEIGHT}px`,
          height: `${totalHeight}px`,
          maxHeight: `${totalHeight}px`,
        }}
        onClick={onClick}
      >
        <div
          className="relative flex w-full h-full justify-center items-center rounded-xs top-0 py-0.5"
          style={{ backgroundColor: color }}
        >
          <HeaderTooltip label={label} isVertical isExpanded={expanded} />
          {typeof toggleReorderModal === 'function' && (
            <HeaderOptionsMenu color={color} toggleReorderModal={toggleReorderModal} isVertical />
          )}
        </div>
      </div>
    );
  },
);

VerticalParentRowHeader.displayName = 'VerticalParentRowHeader';

export const ParentRowHeader = memo(
  ({
    expanded,
    label = '',
    color = DEFAULT_BASE_COLOR,
    hasVerticalSubHeaders,
    onClick,
    toggleReorderModal,
  }: {
    expanded: boolean;
    label?: string;
    color?: string;
    hasVerticalSubHeaders: boolean;
    onClick: () => void;
    toggleReorderModal?: () => void;
  }) => {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: needs to be interactive
      // biome-ignore lint/a11y/useKeyWithClickEvents: needs to be interactive
      <div
        className="group h-full w-full overflow-hidden relative flex items-center p-1 rounded-xs"
        style={{ backgroundColor: color }}
        onClick={onClick}
      >
        <Tooltip>
          <TooltipTrigger asChild className="flex h-full w-full items-center cursor-pointer">
            <div className="flex h-full w-full items-center cursor-pointer">
              {expanded ? (
                <KeyboardArrowUp className="w-6 h-6 text-text-neutral-inverted" />
              ) : (
                <KeyboardArrowDown className="w-6 h-6 text-text-neutral-inverted" />
              )}
              <span
                className={cn(
                  'flex h-full items-center font-semibold text-text-neutral-inverted uppercase text-[0.725rem] min-w-0 overflow-hidden text-ellipsis',
                  {
                    '[writing-mode:vertical-rl] whitespace-normal! h-full p-1':
                      hasVerticalSubHeaders,
                    'whitespace-nowrap': !hasVerticalSubHeaders,
                  },
                )}
              >
                {label}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="end"
            sideOffset={6}
            className="text-sm bg-bg-surface-base text-text-neutral-primary border-2"
          >
            {label}
          </TooltipContent>
        </Tooltip>
        {typeof toggleReorderModal === 'function' && (
          <HeaderOptionsMenu
            color={color}
            toggleReorderModal={toggleReorderModal}
            isVertical={hasVerticalSubHeaders}
          />
        )}
      </div>
    );
  },
);

ParentRowHeader.displayName = 'ParentRowHeader';

export const RowHeader = memo(
  ({
    isChild = false,
    label,
    color,
    isVertical,
    isCompact = false,
    toggleReorderModal,
  }: {
    isChild?: boolean;
    label: string;
    color: string;
    isVertical: boolean;
    isCompact?: boolean;
    toggleReorderModal?: () => void;
  }) => {
    if (isVertical) {
      const buffer = isCompact ? 4 : 8;
      const cellWidth = VERTICAL_ROW_HEADER_WIDTH - buffer;

      return (
        <div
          className={cn(
            'group absolute left-1 flex justify-center min-w-0 bg-bg-surface-base rounded-xs',
            {
              'top-1': !isCompact,
              'top-0': isCompact,
            },
          )}
          style={{
            height: 'calc(100% - 4px)',
            width: `${cellWidth}px`,
            maxWidth: `${cellWidth}px`,
          }}
        >
          <div
            className={cn('relative flex w-full h-full justify-center items-center min-w-0 ', {
              'flex-col': isVertical,
            })}
            style={{
              backgroundColor: color,
            }}
          >
            <HeaderTooltip label={label} isVertical />
            {typeof toggleReorderModal === 'function' && (
              <HeaderOptionsMenu color={color} toggleReorderModal={toggleReorderModal} isVertical />
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'group max-h-full max-w-full h-full w-full flex overflow-hidden min-w-0 rounded-xs bg-bg-surface-base',
          {
            'px-1': !isChild,
          },
        )}
      >
        <div
          className="relative flex w-full h-full justify-center items-center rounded-xs px-1"
          style={{ backgroundColor: color }}
        >
          <HeaderTooltip label={label} isVertical={false} />
          {typeof toggleReorderModal === 'function' && (
            <HeaderOptionsMenu
              color={color}
              toggleReorderModal={toggleReorderModal}
              isVertical={false}
            />
          )}
        </div>
      </div>
    );
  },
);

RowHeader.displayName = 'RowHeader';

export const QuarterHeader = memo(({ quarterLabel }: { quarterLabel: string }) => (
  <div className="p-0.5">
    <div className="border border-border-neutral text-text-neutral bg-bg-neutral rounded-2xl text-center font-semibold py-3 text-sm truncate overflow-hidden">
      {quarterLabel}
    </div>
  </div>
));

QuarterHeader.displayName = 'QuarterHeader';

export const MonthHeader = memo(
  ({ monthName, isMain }: { monthName: string; isMain?: boolean }) => (
    <div className="relative p-1">
      <div
        className={cn(
          'border border-border-neutral text-center font-semibold rounded-sm truncate overflow-hidden',
          {
            'bg-bg-neutral rounded-2xl py-3 font-bold text-text-neutral text-sm': isMain,
            'bg-bg-neutral-subtle rounded-xl py-2 px-1 text-text-neutral-secondary text-xs':
              !isMain,
          },
        )}
      >
        {monthName}
      </div>
    </div>
  ),
);

MonthHeader.displayName = 'MonthHeader';

export const WeekHeader = memo(
  ({
    rangeStart,
    rangeEnd,
    leftOffset,
    width,
    rangeMoreThanHalfYear,
  }: {
    rangeStart: string;
    rangeEnd: string;
    leftOffset: number;
    width: number;
    rangeMoreThanHalfYear?: boolean;
  }) => (
    <div className="relative h-full w-full">
      <div className="absolute p-1 top-0.5" style={{ left: `${leftOffset}px`, width }}>
        <div
          className={cn(
            'overflow-hidden border border-border-neutral text-text-neutral-secondary bg-bg-surface-base py-1.5 px-1 text-center font-semibold rounded-md whitespace-nowrap tracking-tighter',
            {
              'text-xs': !rangeMoreThanHalfYear,
              'text-[10px]': rangeMoreThanHalfYear,
            },
          )}
        >
          {rangeStart} - {rangeEnd}
        </div>
      </div>
    </div>
  ),
);

WeekHeader.displayName = 'WeekHeader';
export const EmptyRowCell = memo(
  ({
    isCompact = false,
    intervalStart,
    intervalEnd,
    showTodayLine,
    dayWidth,
    headerLeftOffset,
  }: {
    isCompact?: boolean;
    intervalStart: Date;
    intervalEnd: Date;
    showTodayLine: boolean;
    dayWidth: number;
    headerLeftOffset: number;
  }) => {
    const today = new Date();
    const shouldRenderTodayLine =
      showTodayLine && isWithinInterval(today, { start: intervalStart, end: intervalEnd });

    return (
      <div
        className="relative w-full"
        style={{
          height: isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT,
          left: headerLeftOffset,
        }}
      >
        {shouldRenderTodayLine && (
          <div
            className="absolute top-0 bottom-0 border-l border-border-error-subtle pointer-events-none z-50"
            style={{
              left: `${differenceInDays(today, intervalStart) * dayWidth}px`,
            }}
          />
        )}
      </div>
    );
  },
);

EmptyRowCell.displayName = 'EmptyRowCell';
